/** Shared display formatting for the portal's tables. Money fields are
 *  Postgres NUMERIC and timestamps come from Python's `str(datetime)` via
 *  `json.dumps(..., default=str)` -- neither is a JS-native type, so both
 *  need defensive formatting rather than a bare `.toFixed()`/`new Date()`.
 */

/** Never throws: a render-time exception costs the whole page. Returns "—"
 *  for null/undefined (no value on file), and the raw string -- not the
 *  literal "NaN" -- if the value isn't actually numeric. */
export function money(value: string | null | undefined): string {
  if (value === null || value === undefined) return "—";
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(2) : String(value);
}

/** The API serializes timestamps with Python's `str(datetime)`, producing
 *  `"2026-07-01 10:00:00+00:00"` -- space-separated, not ISO-8601. V8 parses
 *  that leniently, but no other engine is required to (Safari/JSC returns
 *  Invalid Date), so normalise the separator to "T" before constructing the
 *  Date. Falls back to the raw string -- never the literal "Invalid Date" --
 *  if parsing still fails. */
export function dateTime(value: string): string {
  // Defensive against null/undefined despite the string type: callers render
  // straight off API rows, and a render-time throw here would unmount the
  // whole page (same reasoning as `money`'s guard).
  if (!value) return value ?? "";
  const normalized = value.includes(" ") && !value.includes("T")
    ? value.replace(" ", "T")
    : value;
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}
